import { GlobalStationSearchBarComponent } from '../shared/global-station-search-bar/global-station-search-bar.component';
import { SmwArchiveComponent } from './smw-archive-component';
import { SmwMeteogramComponent } from './smw-meteogram.component';
import { SmwOverviewCardComponent } from './smw-overview-card.component';
import { SmwOverviewMapComponent } from './smw-overview-map.component';
import { SmwTableComponent } from './smw-table.component';

import { AuthService } from '../auth/auth.service';
import { EppService } from '../epp/epp.service';
import { StationService } from '../shared/station.service';
import { SmwService, StationInfo } from './smw.service';

import { KeyPipe } from '../shared/key.pipe';
import { HasUserEppPipe } from './pipes/has-user-epp.pipe';

import { ResetInputMatAutocompleteDirective } from '../shared/directives/reset-input-mat-autocomplete.directive';

import {
  animate,
  keyframes,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { CommonModule, ViewportScroller } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
} from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { TranslateModule } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { MAP_TAB_INDEX } from './constants/constants';
import { TippyDirective } from '@ngneat/helipopper';
import moment from 'moment';
import { map, Observable, startWith, Subscription, timer } from 'rxjs';
import { isNil } from 'src/utils/utils';

interface Station {
  stationId: string;
  overwriteStationName: string;
  stationSource: string;
  stationType: string;
}

interface WebcamStatus {
  [stationId: string]: boolean;
}

export const MODULES = [
  CommonModule,
  MatSidenavModule,
  MatIconModule,
  MatButtonModule,
  ReactiveFormsModule,
  FormsModule,
  MatTabsModule,
  MatCardModule,
  RouterModule,
  MatFormFieldModule,
  MatSelectModule,
  MatInputModule,
  MatAutocompleteModule,
  TranslateModule,
];
export const COMPONENTS = [
  FaIconComponent,
  SmwOverviewCardComponent,
  SmwOverviewMapComponent,
  SmwTableComponent,
  SmwMeteogramComponent,
  SmwArchiveComponent,
  GlobalStationSearchBarComponent,
];
export const PIPES = [HasUserEppPipe];
export const DIRECTIVES = [
  ResetInputMatAutocompleteDirective,
  TippyDirective,
  KeyPipe,
];

@Component({
  selector: 'app-smw',
  templateUrl: './smw.component.html',
  styleUrls: ['./smw.component.scss', './print-smw.component.scss'],
  animations: [
    trigger('shake', [
      state('initial', style('*')),
      state('shake', style('*')),
      transition(
        'initial <=> shake',
        animate(
          '500ms',
          keyframes([
            style({ transform: 'translateX(0)', offset: 0 }),
            style({ transform: 'translateX(-4px)', offset: 0.33 }),
            style({ transform: 'translateX(4px)', offset: 0.66 }),
            style({ transform: 'translateX(0)', offset: 1 }),
          ])
        )
      ),
    ]),
  ],
  imports: [...MODULES, ...COMPONENTS, ...PIPES, ...DIRECTIVES, MatTooltip],
})
export class SmwComponent implements OnInit, AfterViewInit, OnDestroy {
  public auth = inject(AuthService);
  public smwService = inject(SmwService);
  private eppService = inject(EppService);
  private viewportScroller = inject(ViewportScroller);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private stationService = inject(StationService);
  private injector = inject(Injector);

  MAP_TAB_INDEX = MAP_TAB_INDEX;

  cardContainerRo: ResizeObserver | null = null;
  cardContainer = viewChild<ElementRef>('cardContainer');
  smwOverviewMapComponent = viewChild<SmwOverviewMapComponent>(
    SmwOverviewMapComponent
  );

  stationSelectControl = {
    table: new UntypedFormControl(),
    meteogram: new UntypedFormControl(),
    archive: new UntypedFormControl(),
  };

  tabControlIndex = signal<number>(0);
  manualTabControlIndex = signal<number>(undefined);

  archiveOnly = signal(true);
  showMap = signal(false);
  showMeteogram = signal(false);
  stationList = signal([]);
  archiveStationList = signal([]);
  smwStations = signal<Array<StationInfo>>([]);
  latestSmwData = signal<any>(undefined);
  webcamStatus = signal<WebcamStatus>({});
  latestWebcamImages = signal({});
  webcamsWereChecked = signal(false);
  webcamAvailable = signal(false);
  selectedStation = signal<any>(undefined);
  selectedArchiveStation = signal<any>(undefined);
  tableStationListFilteredOptions$: Observable<Station[]>;
  meteogramStationListFilteredOptions$: Observable<Station[]>;
  archiveStationListFilteredOptions$: Observable<Station[]>;
  selectedStationInfo = signal<StationInfo>(undefined);
  stationNames = {};
  smwData = signal<any>(undefined);
  minCardHeight = signal(350);
  sub: Subscription;
  neutralColor = signal('green');
  addedDisplayedOverviewCards = signal(0);
  countOfDisplayedOverviewCards = computed(() => {
    return this.cardsPerRow() * 2 + this.addedDisplayedOverviewCards() * 5;
  });
  cardContainerWidth = signal(0);
  cardsPerRow = computed(() => {
    return Math.floor(this.cardContainerWidth() / 174);
  });

  // Overview cards only: stations without any live data sink to the end,
  // stations with data keep their original relative order.
  overviewStationList = computed(() => {
    const stationList = this.stationList();
    const latestSmwData = this.latestSmwData();
    if (!latestSmwData) {
      return stationList;
    }
    const withData = [];
    const withoutData = [];
    for (const station of stationList) {
      const data = latestSmwData[station.stationId];
      if (data && Object.keys(data).length > 0) {
        withData.push(station);
      } else {
        withoutData.push(station);
      }
    }
    return [...withData, ...withoutData];
  });

  shakeAnimationState = 'initial';

  autoRefresh: Subscription;

  initialSelectedIndex = computed(() => {
    return this.archiveOnly() ? 4 : this.stationList().length > 50 ? 1 : 0;
  });

  actualSelectedIndex = computed(() => {
    const actualIndex = this.tabControlIndex();
    return !isNil(actualIndex) ? actualIndex : this.initialSelectedIndex();
  });

  actualSelectedTab = computed(() => {
    switch (this.actualSelectedIndex()) {
      case 0:
        return 'overview-cards';
      case 1:
        return 'overview-map';
      case 2:
        return 'table';
      case 3:
        return 'meteogram';
      case 4:
        return 'archive';
      default:
        return 'default';
    }
  });

  hasMoreThanFiveStations = computed(() => {
    return this.actualSelectedTab() === 'archive'
      ? this.archiveStationList()?.length > 5
      : this.stationList()?.length > 5;
  });

  constructor() {
    effect(() => {
      const el = this.cardContainer()?.nativeElement;

      this.cardContainerRo?.disconnect();
      this.cardContainerRo = null;

      if (!el) return;

      this.cardContainerWidth.set(el.offsetWidth || 900);

      this.cardContainerRo = new ResizeObserver(entries => {
        const w = entries[0]?.contentRect?.width ?? el.offsetWidth ?? 900;
        this.cardContainerWidth.set(w);
      });

      this.cardContainerRo.observe(el);
    });
  }

  ngOnInit(): void {
    this.setTabControlIndex();
    this.readColorScheme();
    this.getCustomerStations();
    // RELOAD DATA in certain interval
    this.autoRefresh = timer(0, 300000).subscribe(t => {
      this.getLatestSmwData();
      this.getWebcamStatus();
    });
    this.getStationInformationForArchiveStationList();
    this.listenForStationListFilterChanges();
  }

  ngAfterViewInit(): void {
    this.sub = this.route.params.subscribe(params => {
      if (params && params.hasOwnProperty('station')) {
        const station = params['station'];
        this.onNavigateToStationView({ station, tab: 2 });
      }
    });
  }

  readColorScheme(): void {
    const storedColor = localStorage.getItem('eopTableColorScheme');
    let color;
    if (storedColor) {
      color = storedColor;
    } else {
      const userProfile = this.auth.getUserProfile();
      let global = userProfile['metadata']['global'];
      if (!global) {
        global = {};
      }
      if (global.hasOwnProperty('eopTableColorScheme')) {
        color = global['eopTableColorScheme'];
      }
    }
    this.neutralColor.set(color || 'green');
    this.eppService.setGreenTableDesign(this.neutralColor() === 'green');
    this.smwService.greenTableDesign.set(this.neutralColor() === 'green');
  }

  getCustomerStations(): void {
    const customerDocument = this.auth.getCustomerDocument();
    const currentStations = [];
    const currentStationIds = [];
    const archiveStations = [];
    const archiveStationIds = [];
    for (const smwProduct of customerDocument.smwProducts) {
      if (this.auth.isProductActive('smwProducts', smwProduct.id, true)) {
        const isProductActive = this.auth.isProductActive(
          'smwProducts',
          smwProduct.id
        );
        for (const station of smwProduct.stations) {
          if (isProductActive) {
            if (
              !this.auth.isUserAllowedToAccessToProduct(
                smwProduct.id,
                station.stationId
              )
            ) {
              continue;
            }
            this.archiveOnly.set(false);
            if (currentStationIds.indexOf(station.stationId) < 0) {
              currentStationIds.push(station.stationId);
              currentStations.push(station);
            }
          }
          if (archiveStationIds.indexOf(station.stationId) < 0) {
            archiveStationIds.push(station.stationId);
            archiveStations.push(station);
            this.stationNames[station.stationId] =
              station['overwriteStationName'];
          }
        }
      }
    }
    this.stationList.set([
      ...currentStations.sort((a, b) =>
        a.overwriteStationName > b.overwriteStationName ? 1 : -1
      ),
    ]);
    this.archiveStationList.set([
      ...archiveStations.sort((a, b) =>
        a.overwriteStationName > b.overwriteStationName ? 1 : -1
      ),
    ]);
    // Pigeons: start with map
    if (this.stationList().length > 50) {
      this.showMap.set(true);
    }
    if (!this.selectedStation() && this.stationList().length > 0) {
      this.selectedStation.set(this.stationList()[0].stationId);
      this.stationSelectControl.table.setValue(this.selectedStation());
      this.stationSelectControl.meteogram.setValue(this.selectedStation());
      this.getSmwData();
    }
    if (!this.selectedArchiveStation()) {
      if (this.selectedStation()) {
        this.selectedArchiveStation.set(this.selectedStation());
      } else if (this.archiveStationList().length > 0) {
        this.selectedArchiveStation.set(this.archiveStationList()[0].stationId);
      }
      if (this.selectedArchiveStation()) {
        this.stationSelectControl.archive.setValue(
          this.selectedArchiveStation()
        );
      }
    }
  }

  getStationInformationForArchiveStationList(): void {
    this.smwStations.set([]);
    for (const station of this.archiveStationList()) {
      this.getSingleStationInformation(
        station.stationId,
        station.overwriteStationName
      );
    }
  }

  getLatestSmwData(): void {
    const latestSmwData = {};
    const stationIds = this.stationList().map(s => s.stationId);
    const chunkSize = 50;
    let stationCounter = 0;
    for (let i = 0; i < stationIds.length; i += chunkSize) {
      const chunk = stationIds.slice(i, i + chunkSize);
      this.smwService
        .getLatestDataForStations(chunk)
        .toPromise()
        .then(data => {
          for (const stationIdAsString of chunk) {
            const stationId = Number(stationIdAsString);
            if (data.hasOwnProperty(stationId)) {
              latestSmwData[stationId] = data[stationId];
            }
            stationCounter++;
          }
          if (stationCounter >= stationIds.length) {
            this.latestSmwData.set({ ...latestSmwData });
            this.setMinCardHeight();
          }
        });
    }
    /*
    this.smwService.getLatestDataForStations(this.stationList().map((s) => s.stationId)).subscribe(
      data => {
        this.latestSmwData.set({...data});
        this.setMinCardHeight();
      },
      err => console.error('getLatestSmwData', err)
    );

 */
  }

  setMinCardHeight(): void {
    const cardHeight = {
      3: 300,
      4: 310,
      5: 340,
      6: 350,
      7: 365,
    };
    let maxLines = 3;
    const overviewStationList = this.overviewStationList();
    for (let i = 0; i < overviewStationList.length; i++) {
      if (i > this.countOfDisplayedOverviewCards()) {
        break;
      }
      for (const stationId of Object.keys(this.latestSmwData())) {
        if (stationId === overviewStationList[i].stationId) {
          const parameterGroups4Station = this.smwService.latestParameterGroups(
            this.latestSmwData()[stationId]
          );
          const lines = Object.keys(parameterGroups4Station).length;
          maxLines = Math.max(maxLines, lines);
        }
      }
    }
    this.minCardHeight.set(cardHeight[maxLines]);
  }

  getWebcamStatus(): void {
    for (const station of this.stationList()) {
      if (
        this.webcamStatus().hasOwnProperty(station.stationId) &&
        this.webcamStatus()[station.stationId]
      ) {
        this.getSingleStationWebCamStatus(station.stationId);
      }
      this.webcamsWereChecked.set(true);
    }
  }

  getLatestWebcamImage(stationId): void {
    this.smwService.getWebcamImages(stationId, 1).subscribe(
      data => {
        const dtg = moment(data[0]['dtg']);
        const url = data[0]['url'];
        if (dtg.isAfter(moment().subtract(2, 'hours'))) {
          this.latestWebcamImages.update(latestWebcamImages => {
            latestWebcamImages = {
              ...latestWebcamImages,
              [stationId]: this.smwService.transformWebcamUrl(url),
            };
            return { ...latestWebcamImages };
          });
        } else {
          this.latestWebcamImages.update(latestWebcamImages => {
            latestWebcamImages = {
              ...latestWebcamImages,
              [stationId]: null,
            };
            return { ...latestWebcamImages };
          });
        }
      },
      err => console.error('getLatestWebcamImage', err)
    );
  }

  onTabChanged(event: MatTabChangeEvent): void {
    this.manualTabControlIndex.set(event.index);
    if (event.index === 1) {
      if (!this.showMap()) {
        this.showMap.set(true);
      } else {
        this.smwOverviewMapComponent()?.clusterHtmlComponent()?.map?.resize();
      }
    }
    if (!this.showMeteogram() && event.index === 3) {
      this.showMeteogram.set(true);
    }
    if (event.index === 5) {
      this.router.navigate(['/epp', this.selectedStation().stationId]);
    }
  }

  onStationChange(
    event: MatAutocompleteSelectedEvent | MatOptionSelectionChange,
    tab: string
  ): void {
    if (event instanceof MatOptionSelectionChange) {
      if (event.isUserInput) {
        this.updateSelectedStationInTabs(event.source.value, tab);
      }
    } else {
      this.updateSelectedStationInTabs(event.option.value, tab);
    }
  }

  updateSelectedStationInTabs(stationId: string, tab): void {
    if (tab === 'archive') {
      this.selectedArchiveStation.set(stationId);
      if (
        this.stationList().indexOf(
          s => s.stationId === this.selectedArchiveStation()
        )
      ) {
        this.selectedStation.set(stationId);
        const selectedStationInfo = this.smwStations().filter(
          s => s.stationId === this.selectedStation()
        )[0];
        this.selectedStationInfo.set(
          !isNil(selectedStationInfo) ? { ...selectedStationInfo } : undefined
        );
        this.stationSelectControl.table.setValue(this.selectedStation());
        this.stationSelectControl.meteogram.setValue(this.selectedStation());
        this.getSmwData();
      }
    } else {
      this.selectedStation.set(stationId);
      this.selectedArchiveStation.set(stationId);
      const selectedStationInfo = this.smwStations().filter(
        s => s.stationId === this.selectedStation()
      )[0];
      this.selectedStationInfo.set(
        !isNil(selectedStationInfo) ? { ...selectedStationInfo } : undefined
      );
      if (tab === 'table') {
        this.stationSelectControl.meteogram.setValue(this.selectedStation());
        this.stationSelectControl.archive.setValue(this.selectedStation());
      } else if (tab === 'meteogram') {
        this.stationSelectControl.table.setValue(this.selectedStation());
        this.stationSelectControl.archive.setValue(this.selectedStation());
      }
      this.getSmwData();
    }
  }

  onStationCardClick(stationId): void {
    this.selectedStation.set(stationId);
    this.selectedArchiveStation.set(stationId);
    this.manualTabControlIndex.set(2);
    this.stationSelectControl.table.setValue(stationId);
    this.stationSelectControl.meteogram.setValue(stationId);
    this.stationSelectControl.archive.setValue(stationId);

    const selectedStationInfo = this.smwStations().filter(
      s => s.stationId === stationId
    )[0];
    this.selectedStationInfo.set(
      !isNil(selectedStationInfo) ? { ...selectedStationInfo } : undefined
    );
    this.getSmwData();
  }

  onNavigateToStationView(navigate: any): void {
    if (navigate.hasOwnProperty('station') && navigate.hasOwnProperty('tab')) {
      const stationId = navigate['station'];
      this.selectedStation.set(stationId);
      this.stationSelectControl.table.setValue(stationId);
      this.stationSelectControl.meteogram.setValue(stationId);
      this.stationSelectControl.archive.setValue(stationId);
      const selectedStationInfo = this.smwStations().filter(
        s => s.stationId == stationId
      )[0];
      this.selectedStationInfo.set(
        !isNil(selectedStationInfo) ? { ...selectedStationInfo } : undefined
      );
      this.manualTabControlIndex.set(navigate['tab']);
      this.getSmwData();
    }
  }

  getSmwData(): void {
    this.smwData.set(null);
    this.smwService.getSmwData(this.selectedStation()).subscribe(
      data => {
        this.smwData.set([...data]);
      },
      error => console.log('Error SMW getSmwData', error)
    );
  }

  public onClickScroll(elementId: string): void {
    this.viewportScroller.scrollToAnchor(elementId);
  }

  public onClickScrollToStart(): void {
    this.viewportScroller.scrollToPosition([0, -100]);
  }

  onToggleColor(isGreen: boolean) {
    const newColor = isGreen ? 'green' : 'white';
    this.onChangeNeutralColor(newColor);
  }

  public onChangeNeutralColor(color: string): void {
    if (color !== this.neutralColor()) {
      this.neutralColor.set(color);
      this.smwService.greenTableDesign.set(this.neutralColor() === 'green');
      this.eppService.setGreenTableDesign(this.neutralColor() === 'green');
      localStorage.setItem('eopTableColorScheme', color);
      const userProfile = this.auth.getUserProfile();
      if (userProfile) {
        let global = userProfile['metadata']['global'];
        if (!global) {
          global = {};
        }
        global['eopTableColorScheme'] = this.neutralColor();
        this.auth.updateUserData('global', global);
      }
    }
  }

  public onClickStartAnimation(): void {
    this.shakeAnimationState =
      this.shakeAnimationState === 'initial' ? 'shake' : 'initial';
  }

  onGlobalStationSelect(
    stationId: number,
    stationName: string,
    nameOfSource: string,
    tab: string
  ): void {
    const stationIdString = stationId + '';
    this.selectedStation.set(stationIdString);

    // request stationInformation for the global selected station
    this.stationService.getStationInfo(stationIdString).subscribe(
      data => {
        const stationInfo: StationInfo = data;
        stationInfo.name = stationName;
        stationInfo.gmaAlarm = this.smwService.isGmaAlarmForStationOrdered(
          stationIdString,
          true
        );

        // add requested global station to existing swmStations
        this.smwStations.set([...this.smwStations(), stationInfo]);

        // get latest smw data for global selected station
        this.smwService.getLatestDataForStations([stationIdString]).subscribe(
          data => {
            // add requested global station latest smw data to already existing data
            this.latestSmwData.update(latestSmwData => ({
              ...latestSmwData,
              [stationId]: data[stationId],
            }));
            // create station object
            const station: Station = {
              stationId: stationIdString,
              overwriteStationName: stationName,
              stationSource: 'global',
              stationType: nameOfSource,
            };

            // add global station to station list once smw data is there
            this.addSingleStationToStationList(station);
            this.addSingleStationToArchiveStationList(station);

            this.getSingleStationWebCamStatus(stationIdString);

            if (
              stationInfo.stationId === this.selectedStation() ||
              stationInfo.stationId === this.selectedArchiveStation()
            ) {
              this.selectedStationInfo.set({ ...stationInfo });
            }

            // set the select inputs to the selected global station
            this.setGlobalStationInStationSelectInputField(tab);
            // select global station in other tabs
            this.updateSelectedStationInTabs(this.selectedStation(), tab);
            this.getSingleStationWebCamStatus(stationIdString);

            if (
              stationInfo.stationId === this.selectedStation() ||
              stationInfo.stationId === this.selectedArchiveStation()
            ) {
              this.selectedStationInfo.set({ ...stationInfo });
            }

            // set the select inputs to the selected global station
            this.setGlobalStationInStationSelectInputField(tab);
            // select global station in other tabs
            this.updateSelectedStationInTabs(this.selectedStation(), tab);

            this.setMinCardHeight();
          },
          err => console.error('getLatestSmwData', err)
        );
      },
      err => console.error('getStationInformation', err)
    );
  }

  addSingleStationToStationList(station: Station): void {
    if (!this.stationList().some(s => s.stationId === station.stationId)) {
      this.stationList.update(stationList => {
        return [...stationList, station];
      });
    }
  }
  addSingleStationToArchiveStationList(station: Station): void {
    if (
      !this.archiveStationList().some(s => s.stationId === station.stationId)
    ) {
      this.archiveStationList.update(archiveStationList => {
        return [...archiveStationList, station];
      });
    }
  }

  setGlobalStationInStationSelectInputField(tab: string): void {
    if (tab === 'table') {
      this.stationSelectControl.table.setValue(this.selectedStation());
    }
    if (tab === 'meteogram') {
      this.stationSelectControl.meteogram.setValue(this.selectedStation());
    }
    if (tab === 'archive') {
      this.stationSelectControl.archive.setValue(this.selectedStation());
    }
  }

  getSingleStationInformation(stationId, stationName): void {
    this.stationService.getStationInfo(stationId).subscribe(
      data => {
        const stationInfo = data;
        stationInfo.name = stationName;
        stationInfo.gmaAlarm =
          stationInfo['type'] === '2' || stationInfo['type'] === '3'
            ? this.smwService.isGmaAlarmForStationOrdered(stationId, true)
            : false;
        this.smwStations.set([...this.smwStations(), stationInfo]);
        if (
          stationInfo.stationId === this.selectedStation() ||
          stationInfo.stationId === this.selectedArchiveStation()
        ) {
          this.selectedStationInfo.set({ ...stationInfo });
        }
        if (stationInfo['type'] === '2' || stationInfo['type'] === '3') {
          this.getSingleStationWebCamStatus(stationId);
        }
      },
      err => console.error('Error getSingleStationInformation', err)
    );
  }

  getSingleStationWebCamStatus(stationId: string): void {
    this.smwService.getWebcamStatus([stationId]).subscribe(
      data => {
        if (data.hasOwnProperty(stationId)) {
          this.webcamStatus.update(webcamStatus => {
            webcamStatus[stationId] = data[stationId];
            return { ...webcamStatus };
          });
          if (data[stationId]) {
            this.webcamAvailable.set(true);
            this.getLatestWebcamImage(stationId);
          } else {
            this.latestWebcamImages.update(latestWebcamImages => {
              latestWebcamImages = {
                ...latestWebcamImages,
                [stationId]: null,
              };
              return { ...latestWebcamImages };
            });
          }
        } else {
          this.latestWebcamImages.update(latestWebcamImages => {
            latestWebcamImages = {
              ...latestWebcamImages,
              [stationId]: null,
            };
            return { ...latestWebcamImages };
          });
        }

        this.webcamsWereChecked.set(true);
      },
      err => console.error('getWebcamStatus', err)
    );
  }

  ngOnDestroy(): void {
    if (this.autoRefresh) {
      this.autoRefresh.unsubscribe();
    }
  }

  displayStationName(stationId: string, tab: string): string {
    let result = '';
    if (tab === 'archive') {
      const station = this.archiveStationList().find(
        s => s.stationId == stationId
      );
      const overwriteStationName = station?.overwriteStationName;
      if (!isNil(overwriteStationName)) result = overwriteStationName;
    } else {
      const station = this.stationList().find(s => s.stationId == stationId);
      const overwriteStationName = station?.overwriteStationName;
      if (!isNil(overwriteStationName)) result = overwriteStationName;
    }

    return result;
  }

  private setTabControlIndex() {
    effect(
      () => {
        const manual = this.manualTabControlIndex();

        untracked(() => {
          if (!isNil(manual)) {
            this.tabControlIndex.set(manual);
            return;
          }

          this.tabControlIndex.set(
            this.archiveOnly() ? 4 : this.stationList().length > 50 ? 1 : 0
          );
        });
      },
      { injector: this.injector }
    );
  }

  private listenForStationListFilterChanges() {
    this.tableStationListFilteredOptions$ = this.stationSelectControl[
      'table'
    ].valueChanges.pipe(
      startWith(''),
      map(searchTerm => {
        const station = this.stationList().find(s => s.stationId == searchTerm);
        const displayingStationName = isNil(station)
          ? searchTerm
          : this.displayStationName(station, 'table');
        return !isNil(displayingStationName)
          ? this.filterStationsByOverwriteStationName(
              displayingStationName,
              'table'
            )
          : this.stationList().slice();
      })
    );

    this.meteogramStationListFilteredOptions$ = this.stationSelectControl[
      'meteogram'
    ].valueChanges.pipe(
      startWith(''),
      map(searchTerm => {
        const station = this.stationList().find(s => s.stationId == searchTerm);
        const displayingStationName = isNil(station)
          ? searchTerm
          : this.displayStationName(station, 'meteogram');
        return !isNil(displayingStationName)
          ? this.filterStationsByOverwriteStationName(
              displayingStationName,
              'meteogram'
            )
          : this.stationList().slice();
      })
    );

    this.archiveStationListFilteredOptions$ = this.stationSelectControl[
      'archive'
    ].valueChanges.pipe(
      startWith(''),
      map(searchTerm => {
        const station = this.archiveStationList().find(
          s => s.stationId == searchTerm
        );
        const displayingStationName = isNil(station)
          ? searchTerm
          : this.displayStationName(station, 'archive');
        return !isNil(displayingStationName)
          ? this.filterStationsByOverwriteStationName(
              displayingStationName,
              'archive'
            )
          : this.stationList().slice();
      })
    );
  }

  private filterStationsByOverwriteStationName(
    displayingStationName: string,
    tab: string
  ): Station[] {
    const filterValue = displayingStationName.toLowerCase();

    if (tab === 'archive') {
      return this.archiveStationList().filter(station =>
        this.displayStationName(station.stationId, tab)
          .toLowerCase()
          .includes(filterValue)
      );
    } else {
      return this.stationList().filter(station =>
        this.displayStationName(station.stationId, tab)
          .toLowerCase()
          .includes(filterValue)
      );
    }
  }
}
